import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from 'convex/react';
import { useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { UserButton } from '@clerk/react';
import { api } from '@packages/backend/convex/_generated/api';
import { ArrowRight, Cat, Dog } from 'lucide-react';
import { m } from '@/paraglide/messages.js';
import { SettingsDrawer } from '@/components/SettingsDrawer';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

export const Route = createFileRoute('/_authenticated/new')({
  component: NewUserPage,
});

function NewUserPage() {
  const navigate = useNavigate();
  const profile = useQuery(api.tutor.queries.getMyProfile);
  const patients = useQuery(api.tutor.queries.listMyPatients);
  const ongoingSubmission = useQuery(api.tutor.queries.getMyOngoingSubmission);

  const firstName = profile?.firstName ?? '';
  const createPatient = useMutation(api.tutor.mutations.createMyPatient);
  const createSubmission = useMutation(api.tutor.mutations.createSubmission);

  // Form validation state
  const [nameError, setNameError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Synchronous guard against double-clicks (useRef, not useState, to avoid stale-closure race)
  const submitting = useRef(false);

  const getErrorMessage = (err: unknown) => {
    if (err instanceof Error && err.message) return err.message;
    return 'Failed to create submission';
  };

  if (patients === undefined || profile === undefined) {
    return <LoadingSkeleton />;
  }

  if (!profile?.onboardingCompletedAt) {
    return <Navigate to="/onboarding" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 pt-8 pb-12 sm:px-6 lg:px-8 ">
      {import.meta.env.DEV && (
        <div className="fixed top-3 right-3 z-50">
          <UserButton />
        </div>
      )}
      <h1 className="text-xl font-semibold capitalize mb-8">
        {m.newPatient_welcome({ name: firstName })}
      </h1>
      <div className="flex justify-center">
        <div className="w-full max-w-2xl space-y-6">
          {/* Continue ongoing form - shown if user has an incomplete submission */}
          {ongoingSubmission?.submission && ongoingSubmission.patient && (
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader className="space-y-1">
                <CardTitle className="text-start text-2xl font-bold">
                  {m.newPatient_ongoingFormFor({
                    name: ongoingSubmission.patient.firstName,
                  })}
                </CardTitle>
              </CardHeader>
              <CardFooter>
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() =>
                    navigate({
                      to: '/questionnaire/$submissionId',
                      params: {
                        submissionId: ongoingSubmission.submission._id,
                      },
                    })
                  }
                >
                  {m.newPatient_continueForm()}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* Add new animal card - shown if no ongoing submission */}
          {!ongoingSubmission?.submission && (
            <Card>
              <CardHeader className="space-y-1">
                <CardTitle className="text-start text-2xl font-bold">
                  {patients.length === 0
                    ? m.newPatient_addFirstAnimal()
                    : m.newPatient_addNewAnimal()}
                </CardTitle>
              </CardHeader>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (submitting.current) return;
                  const formData = new FormData(e.currentTarget);
                  const patientFirstName = formData.get('firstName') as string;

                  if (!patientFirstName.trim()) {
                    setNameError(true);
                    return;
                  }

                  setError(null);
                  submitting.current = true;
                  void (async () => {
                    try {
                      const patientId = await createPatient({
                        firstName: patientFirstName.trim(),
                      });

                      const submissionId = await createSubmission({
                        patientId,
                      });

                      setError(null);
                      navigate({
                        to: '/questionnaire/$submissionId',
                        params: { submissionId },
                      });
                    } catch (err) {
                      console.error(
                        'Failed to create patient/submission:',
                        err,
                      );
                      // Capture to Sentry — the tutor `beforeSend` filter
                      // drops `ConvexError` with `expected: true` (normal
                      // user-recoverable failures), so only system invariant
                      // violations actually reach the dashboard.
                      Sentry.withScope((scope) => {
                        scope.setTag('tutor.flow', 'new_patient');
                        Sentry.captureException(err);
                      });
                      submitting.current = false;
                      setError(getErrorMessage(err));
                    }
                  })();
                }}
              >
                <CardContent className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder={m.newPatient_animalFirstNamePlaceholder()}
                      autoFocus
                      onChange={() => {
                        if (nameError) setNameError(false);
                        if (error) setError(null);
                      }}
                    />
                    {nameError && (
                      <p className="text-sm text-destructive">
                        {m.newPatient_animalFirstNameRequired()}
                      </p>
                    )}
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col space-y-3">
                  <Button type="submit" size="lg" className="mt-6 w-full">
                    {m.newPatient_continue()}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          )}

          {/* Existing animals - shown below if any exist */}
          {patients.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-start text-2xl font-bold">
                  {m.newPatient_yourAnimals({ count: patients.length })}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 mt-2 px-4">
                {patients.map((patient) => {
                  return (
                    <Card
                      key={patient._id}
                      className="transition-colors w-full hover:bg-muted/50 cursor-pointer py-4"
                    >
                      <CardContent className="px-4">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-3">
                            {patient.species === 'cat' ? (
                              <Cat
                                className="w-8 h-8 shrink-0"
                                strokeWidth={1.5}
                              />
                            ) : (
                              <Dog
                                className="w-8 h-8 shrink-0"
                                strokeWidth={1.5}
                              />
                            )}
                            <h4 className="font-semibold text-lg truncate min-w-0">
                              {patient.firstName} {patient.lastName}
                            </h4>
                          </div>

                          {/* Action Button */}
                          <Button
                            className="w-full"
                            onClick={() => {
                              if (submitting.current) return;
                              setError(null);
                              submitting.current = true;
                              void (async () => {
                                try {
                                  const submissionId = await createSubmission({
                                    patientId: patient._id,
                                  });
                                  setError(null);
                                  navigate({
                                    to: '/questionnaire/$submissionId',
                                    params: { submissionId },
                                  });
                                } catch (err) {
                                  console.error(
                                    'Failed to create submission for existing patient:',
                                    err,
                                  );
                                  Sentry.withScope((scope) => {
                                    scope.setTag(
                                      'tutor.flow',
                                      'existing_patient',
                                    );
                                    Sentry.captureException(err);
                                  });
                                  submitting.current = false;
                                  setError(getErrorMessage(err));
                                }
                              })();
                            }}
                          >
                            {m.newPatient_newForm()}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 flex justify-center pb-6">
        <SettingsDrawer />
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 px-4 pt-8 pb-12 sm:px-6 lg:px-8">
      <div className="flex justify-center">
        <div className="w-full max-w-2xl space-y-6">
          {/* Add new animal skeleton */}
          <Card>
            <CardHeader className="space-y-1">
              <Skeleton className="mx-auto h-8 w-64" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
            <CardFooter>
              <Skeleton className="h-11 w-full" />
            </CardFooter>
          </Card>

          {/* Existing animals skeleton */}
          <Card>
            <CardHeader>
              <Skeleton className="mx-auto h-8 w-32" />
              <Skeleton className="mx-auto h-4 w-64" />
            </CardHeader>
            <CardContent className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="flex-1">
                        <Skeleton className="mb-2 h-5 w-32" />
                        <Skeleton className="h-4 w-48" />
                      </div>
                      <Skeleton className="h-9 w-24" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
